import { useTheme } from '@evaneos/b2b-ui';
import { NavLink } from 'react-router-dom';
import type { ComponentProps, PropsWithChildren } from 'react';

type Props = ComponentProps<typeof NavLink> & {
    disabled?: boolean;
    'data-spec-id'?: string;
};

export const AppointmentNavLink = ({
    disabled,
    'data-spec-id': dataSpecId,
    ...props
}: PropsWithChildren<Props>) => {
    const theme = useTheme();

    const baseStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        height: 48,
        padding: '0 16px',
        fontSize: '1.4rem',
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        textDecoration: 'none',
        borderBottom: '2px solid transparent',
    } as const;

    if (disabled) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars -- strip NavLink-only `to`/`style`/`className` to avoid invalid props on <a>
        const { to, style, className, children, ...anchorProps } = props;
        return (
            <a
                aria-disabled="true"
                tabIndex={-1}
                data-spec-id={dataSpecId}
                style={{ ...baseStyle, opacity: 0.38, cursor: 'default' }}
                className={typeof className === 'string' ? className : undefined}
                {...anchorProps}
            >
                {children}
            </a>
        );
    }

    return (
        <NavLink
            data-spec-id={dataSpecId}
            style={({ isActive }) => ({
                ...baseStyle,
                ...(isActive && {
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    borderBottomColor: theme.palette.primary.main,
                }),
            })}
            {...props}
        />
    );
};
